import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
import moment from "moment";

import Fmsync from "../../api/Fmsync";
import Wizard from "./Wizard";
import Details from "./Details";
import Donation from "./Donation";
import PaymentTab from "./Payment";
import Modal from "../Modals";
import Loading from "../Loading";
import { checkAuth, setSessionData } from "../../actions/session";
import { SaveButton as Button } from "../Button";

import { displayAddress } from "../../helpers/Address";

import {
  composeValidators,
  isValidPhoneNumber,
  minValue,
  required,
} from "./Shared";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  storeCreditCard,
  storeDirectDebit,
  validateCC,
  validateDD,
} from "../../api/Bpoint";
import { Auth } from "../../api";

const tomorrowsDate = moment().add(1, "d");

const CUSTOM_QUESTION = "Custom question";

const validateStep1 = (values) => {
  const errors = {};
  errors.mission_id = required(values.mission_id)
    ? "Please select an staff worker or campus"
    : undefined;
  errors.frequency = required(values.frequency)
    ? "Please select a frequency"
    : undefined;
  errors.amount = composeValidators(required, minValue(10))(values.amount)
    ? "Minimum support is $10"
    : undefined;
  return errors;
};

const validateStep2 = (values) => {
  const errors = {};
  errors.address =
    required(values.address_line_1) ||
    required(values.state) ||
    required(values.zip) ||
    required(values.country)
      ? "Please enter your address"
      : undefined;
  if (values.phone?.length) {
    errors.phone = isValidPhoneNumber(values.phone);
  }
  if (values.mobile?.length) {
    errors.mobile = isValidPhoneNumber(values.mobile);
  }
  errors.mobile =
    required(values.phone) && required(values.mobile)
      ? "Please enter at least one contact number"
      : errors.mobile;
  return errors;
};

const validateStep3 = (values) => {
  const errors = {};
  if (values.payment_type === "Direct Debit") {
    errors.AcceptBADirectDebitTC =
      required(values.AcceptBADirectDebitTC) ||
      values.AcceptBADirectDebitTC !== true
        ? "You must agree to the terms and conditions to continue"
        : undefined;
  }

  return errors;
};

function Payment() {
  const [support, setSupport] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [errors, setErrors] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isAuthedOrGuest, setIsAuthedOrGuest] = useState(false);

  const { userId, userdata: authedUser } = useSelector(
    (state) => state.session
  );

  const location = useLocation();
  const navigate = useNavigate();

  const wizRef = useRef();
  const recaptchaRef = useRef();

  const dispatch = useDispatch();
  const params = useParams();

  const charity = useMemo(() => location.pathname, [location.pathname]);
  const isAuthed = useMemo(() => !!userId, [userId]);

  const editDetails = () => {
    setConfirmModal(false);
    wizRef.current.goto(1);
  };

  const editMission = () => {
    setConfirmModal(false);
    wizRef.current.goto(0);
  };

  const onSubmit = async (v) => {
    setConfirmModal(true);
    setValues(v);
  };

  const processSupport = async () => {
    setSubmitting(true);
    setErrors(false);
    recaptchaRef.current.reset();
    recaptchaRef.current.execute();
  };

  const processCaptchaError = (e) => {
    console.error(e, "CaptchaError", recaptchaRef.current);
    setSubmitting(false);
    setErrors(
      "There has been an issue dealing with the Captcha Service, please try again in a few moments."
    );
  };

  const processCaptchaExpired = (e) => {
    console.error(e, "CaptchaError", recaptchaRef.current);
    setSubmitting(false);
    setErrors(
      "This process is taking longer than expected, please wait for a few more moments"
    );
  };

  const processCaptchaResponse = useCallback(
    async (token, payload) => {
      try {
        if (token === null) {
          recaptchaRef.current.reset();
          recaptchaRef.current.execute();
          return;
        }

        const { noThanks, signUp } = payload;

        let config = undefined;
        if (noThanks || signUp) {
          if (noThanks) {
            delete payload.password;
            delete payload.password_confirmation;
          }

          try {
            const registerResponse = await Auth.register(payload);
            config = {
              headers: {
                Authorization: `Bearer ${registerResponse.data.token}`,
              },
            };
            if (signUp) {
              const addQuestions = {
                questions: [
                  {
                    question:
                      payload.question === CUSTOM_QUESTION
                        ? payload.custom_question
                        : payload.question,
                    answer: payload.secret_answer,
                  },
                ],
                password: payload.password,
              };
              await Auth.addRecoveryQuestion(addQuestions, config);
            }
            const session = await Auth.getSession(config);

            if (session?.verified) {
              dispatch(setSessionData(session));
            }
          } catch (e) {
            if (e?.message !== "Already authenticated") {
              throw new Error(e);
            }
          }
        }

        const processDonateToDB = async (donationValues) => {
          delete donationValues.cc_cvv;
          delete donationValues.cc_expiry;
          delete donationValues.cc_number;
          delete donationValues.credit_card_ccv;
          delete donationValues.credit_card_expiry_date;
          delete donationValues.credit_card_number;
          delete donationValues.direct_debit_bsb;
          delete donationValues.direct_debit_number;
          delete donationValues.bsb_number;
          delete donationValues.account_number;
          delete donationValues.reports;

          const result = await Fmsync.donate({ ...donationValues }, config);
          setSubmitting(false);
          setConfirmModal(false);
          localStorage.setItem(
            "success",
            JSON.stringify({
              ...result.data.data,
              donorFirstName:
                donationValues.organisation_name || donationValues.first_name,
            })
          );
          navigate(`/thank-you`);
        };

        switch (payload.payment_type) {
          case "Credit Card": {
            delete payload.prefered_dd_payment;
            delete payload.prefered_dd_date;
            if (payload.prefered_cc_payment === "new") {
              payload.credit_card_name =
                payload.credit_card_name || payload.cc_name;
              payload.credit_card_number =
                payload.credit_card_number || payload.cc_number;
              payload.credit_card_expiry_date =
                payload.credit_card_expiry_date ||
                moment(payload.cc_expiry, "MM/YY").format("MM/YYYY");
              payload.credit_card_ccv =
                payload.credit_card_ccv || payload.cc_cvv;
              await validateCC(payload);
              await storeCreditCard(
                payload,
                async (response) => {
                  payload.prefered_cc_payment =
                    response.accounts.credit_card.id;
                  await processDonateToDB(payload);
                },
                (bPointErrorResponse) => {
                  payload.bpointError = `BPOINT: ${Object.values(
                    bPointErrorResponse
                  ).join(". ")}`;
                  setErrors(Object.values(bPointErrorResponse).join(". "));
                  setSubmitting(false);
                },
                config
              );
            }
            break;
          }
          case "Direct Debit": {
            delete payload.prefered_cc_payment;
            delete payload.prefered_cc_date;

            if (payload.prefered_dd_payment === "new") {
              payload.direct_debit_name =
                payload.direct_debit_name || payload.account_holder;
              payload.direct_debit_number = payload.account_number.replace(
                / /g,
                ""
              );
              payload.direct_debit_bsb = payload.bsb_number.replace(/ /g, "");
              await validateDD(payload);
              await storeDirectDebit(
                payload,
                async (response, result) => {
                  // add BPOINT details for donation logs...
                  const { RedirectionUrl, ...bpointResponse } = result;
                  const url = new URL(RedirectionUrl);
                  // Get the search params from the URL
                  const searchParams = new URLSearchParams(url.search);
                  // Get the value of the "ResponseText" parameter
                  const responseText = searchParams.get("ResponseText");
                  payload.bpoint = {
                    agreedToTerms: payload.AcceptBADirectDebitTC ? "Yes" : "No",
                    response: { ...bpointResponse, responseText },
                    account: {
                      accountName: payload.direct_debit_name,
                      truncatedAccountNumber:
                        payload.accounts?.direct_debit?.number ||
                        `${payload.bsb_number}###${payload.account_number
                          .toString()
                          .slice(-3)}`,
                    },
                  };
                  payload.prefered_dd_payment =
                    response.accounts.direct_debit.id;
                  await processDonateToDB(payload);
                },
                (bPointErrorResponse) => {
                  payload.bpointError = `BPOINT: ${Object.values(
                    bPointErrorResponse
                  ).join(". ")}`;
                  setErrors(Object.values(bPointErrorResponse).join(". "));
                  setSubmitting(false);
                },
                config
              );
            }

            break;
          }
          default:
            delete payload.prefered_cc_payment;
            delete payload.prefered_cc_date;
            delete payload.prefered_dd_payment;
            delete payload.prefered_dd_date;
            break;
        }

        if (
          payload.prefered_dd_payment !== "new" &&
          payload.prefered_cc_payment !== "new"
        ) {
          await processDonateToDB(payload);
          return;
        }
      } catch (e) {
        console.error(e);
        setSubmitting(false);
        if ("errors" in e) {
          setErrors(e.errors[0] || Object.values(e.errors)[0]);
        } else if (e.message?.includes("BPOINT: ")) {
          setErrors(e.message.replace("BPOINT: ", ""));
        } else {
          //try to get first error message...
          const firstValue = e[Object.keys(e)[0]];
          setErrors(
            firstValue ||
              "An unexpected error occurred, please try again or reload the screen"
          );
        }
      }
    },
    [dispatch, navigate]
  );

  const handleLogin = useCallback(
    (data, currentValues) => {
      dispatch(setSessionData(data));
      setTimeout(() => {
        setLoading(true);
        setInitialValues({
          ...currentValues,
          ...data,
          find_my_address: displayAddress(data).display,
          prefered_dd_payment: String(
            data?.accounts?.direct_debit?.id || "new"
          ),
          prefered_cc_payment: String(data?.accounts?.credit_card?.id || "new"),
          page: 1,
        });
        setTimeout(() => setLoading(false), 1000);
      }, 300);
    },
    [dispatch]
  );

  const { CreditCard, DirectDebit } = useMemo(
    () => ({
      CreditCard: authedUser?.accounts?.credit_card,
      DirectDebit: authedUser?.accounts?.direct_debit,
    }),
    [authedUser]
  );

  useEffect(() => {
    let obj = {
      prefered_debit_date: tomorrowsDate,
      prefered_cc_date: tomorrowsDate,
      payment_type: "Direct Debit",
      is_organisation: "0",
      prefered_dd_payment: DirectDebit?.id ? String(DirectDebit.id) : "new",
      prefered_cc_payment: CreditCard?.id ? String(CreditCard.id) : "new",
      mobile: "",
      phone: "",
      ...authedUser,
    };

    if (
      obj.honorific &&
      !["mr", "mrs", "ms", "miss"].includes(obj.honorific.toLowerCase())
    ) {
      obj.honorific_other = obj.honorific;
      obj.honorific = "other";
    } else if (!obj.honorific) {
      obj.honorific = "";
    }
    obj.find_my_address = displayAddress(obj).display;
    if (charity) {
      const selectionParts = charity.split("-");
      if (selectionParts.shift() === "/support") {
        const missionID = selectionParts.pop();
        const foundCharity = support.find(
          (obj) => String(obj.id) === String(missionID)
        );
        if (foundCharity?.id) {
          obj.mission_id = foundCharity.id;
          obj.mission_name = foundCharity.name;
          obj.donorlist = foundCharity.name;
        }
      }
    }
    obj.is_organisation = obj.organisation_name ? "1" : "0";
    setInitialValues((prev) => ({ ...prev, ...obj }));
  }, [authedUser, charity, support, CreditCard, DirectDebit]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await Fmsync.getSupportList("?filter[published]=1");
        setSupport(res.data.data);
        if (params?.charity) {
          const myCharity = params.charity.split("-").pop();
          const found = res.data.data.find(
            (charity) => String(charity.id) === myCharity
          );
          if (found?.id) {
            setInitialValues((prev) => ({
              ...prev,
              donorlist: found.name,
              mission_id: found.id,
              mission_name: found.name,
            }));
          }
        }
        await dispatch(checkAuth);
      } catch (error) {
        //
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dispatch, params]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div>
      <h1 className="white">New Financial Partnership</h1>
      <Wizard
        ref={wizRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        isAuthedOrGuest={isAuthedOrGuest}
        page={initialValues?.page || 0}
      >
        <Wizard.Page validate={validateStep1}>
          <Donation support={support} />
        </Wizard.Page>
        <Wizard.Page validate={validateStep2}>
          <Details
            handleLogin={handleLogin}
            setIsAuthedOrGuest={setIsAuthedOrGuest}
            canType={isAuthedOrGuest}
            isAuthed={isAuthed}
          />
        </Wizard.Page>
        <Wizard.Page validate={validateStep3}>
          <PaymentTab
            isAuthed={isAuthed}
            DirectDebit={DirectDebit}
            CreditCard={CreditCard}
          />
        </Wizard.Page>
      </Wizard>
      {confirmModal && (
        <Modal
          hasCloseButton={false}
          isOpen={confirmModal}
          modalName="payment_summary"
          onClose={() => {
            setConfirmModal(false);
            setErrors(false);
          }}
        >
          <div className="payment_summary__wrap">
            <div className="payment_summary__block">
              <h4>
                Summary{" "}
                <span>
                  <button className="btn btn__link--text" onClick={editMission}>
                    Edit
                  </button>
                </span>
              </h4>
              <p className="payment_summary__head">
                You're about to support <span>{values.mission_name}</span> with
                a{" "}
                <span>
                  ${values.amount}{" "}
                  <span className="lowercase">{values.payment_schedule}</span>
                </span>{" "}
                payment.
              </p>
            </div>
            <div className="payment_summary__block">
              <h4>
                Your Details{" "}
                <span>
                  <button className="btn btn__link--text" onClick={editDetails}>
                    Edit
                  </button>
                </span>
              </h4>
              {values.is_organisation === "1" && (
                <p>
                  On behalf of: <strong>{values.organisation_name}</strong>
                </p>
              )}
              <p>
                {values.honorific === "other"
                  ? values.honorific_other
                  : values.honorific}
                &nbsp;
                {values.first_name}&nbsp;
                {values.last_name}
              </p>
              <p>{values.email}</p>
              {values.mobile ? <p>Mob: {values.mobile}</p> : ""}
              {values.phone ? <p>Tel: {values.phone}</p> : ""}
              <p>{displayAddress(values).display}</p>
            </div>
            {errors && (
              <div className="payment_summary__error_wrap">
                <span className="payment_summary__error">{errors}</span>
              </div>
            )}
          </div>
          <div className="buttons">
            <ReCAPTCHA
              onErrored={processCaptchaError}
              onExpired={processCaptchaExpired}
              onChange={(token) => processCaptchaResponse(token, values)}
              ref={recaptchaRef}
              sitekey="6Lc7RCMUAAAAAGT3GlE3QXMzwuThsImOPvsdzGsz"
              size="invisible"
            />

            {submitting ? (
              <Button
                disabled
                variant="saving"
                className="btn__solid save-button is-saving"
              >
                Processing...
              </Button>
            ) : (
              <React.Fragment>
                <button
                  disabled={submitting}
                  type="button"
                  className="btn__solid save-button"
                  onClick={() => {
                    setConfirmModal(false);
                    setErrors(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={processSupport}
                  type="button"
                  className="btn__solid save-button"
                >
                  Confirm My Support
                </button>
              </React.Fragment>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Payment;
